import { Link, useLocation } from "@tanstack/react-router";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, ChevronDown } from "lucide-react";
import { BookDemoButton } from "@/components/book-demo-dialog";
import { signInUrl } from "@/lib/cta";
import logoWhite from "@/assets/logo-white.webp";

const useCasesByCompany = [
  { slug: "high-growth-startups", label: "High-growth startups" },
  { slug: "b2b-saas", label: "B2B SaaS" },
  { slug: "mid-market-enterprise", label: "Mid-market enterprises" },
  { slug: "venture-studios", label: "Venture studios" },
  { slug: "agencies-studios", label: "Agencies & studios" },
];
const useCasesByIndustry = [
  { slug: "law", label: "Law firms" },
  { slug: "cybersecurity", label: "Cybersecurity companies" },
  { slug: "real-estate-construction", label: "Real estate & construction" },
  { slug: "logistics", label: "Logistics" },
  { slug: "web3", label: "Web3 projects" },
  { slug: "fitness", label: "Fitness & wellness brands" },
  { slug: "ecommerce", label: "Ecommerce & luxury brands" },
];
const productsIntelligence = [{ slug: "market-intelligence", label: "Market intelligence" }];
const productsAnalytics = [
  { slug: "ai-visibility-analytics", label: "AI visibility analytics" },
  { slug: "cross-channel-social-analytics", label: "Cross-channel social analytics" },
];
const productsAgentic = [
  { slug: "geo-aeo-seo-autopilot", label: "GEO / AEO / SEO autopilot" },
  { slug: "linkedin-autopilot", label: "LinkedIn autopilot" },
  { slug: "x-autopilot", label: "X autopilot" },
];
const resources = [
  { to: "/blog", label: "Blog" },
  { to: "/market-reports", label: "Market reports" },
  { to: "/tutorials", label: "Tutorials" },
  { to: "/geo", label: "GEO fundamentals" },
  { to: "/comparisons", label: "Comparisons" },
  { to: "/affiliates", label: "Affiliates" },
  { to: "/about", label: "About" },
] as const;
const primaryLinks = [
  { to: "/case-studies", label: "Case studies" },
  { to: "/pricing", label: "Pricing" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const pageSlug = useLocation({ select: (l) => l.pathname });

  return (
    <header className="sticky top-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center" aria-label="Axy.digital home">
            <img src={logoWhite} alt="Axy.digital" width={225} height={91} className="h-7 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-white/70">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:text-white transition-colors outline-none">
                Use cases <ChevronDown className="size-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem asChild>
                  <Link to="/use-cases">All use cases</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                  By company type
                </DropdownMenuLabel>
                {useCasesByCompany.map((u) => (
                  <DropdownMenuItem key={u.slug} asChild>
                    <Link to="/use-cases/$slug" params={{ slug: u.slug }}>
                      {u.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                  By industry
                </DropdownMenuLabel>
                {useCasesByIndustry.map((u) => (
                  <DropdownMenuItem key={u.slug} asChild>
                    <Link to="/use-cases/$slug" params={{ slug: u.slug }}>
                      {u.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:text-white transition-colors outline-none">
                Products <ChevronDown className="size-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/products">All products</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                  Intelligence
                </DropdownMenuLabel>
                {productsIntelligence.map((p) => (
                  <DropdownMenuItem key={p.slug} asChild>
                    <Link to="/products/$slug" params={{ slug: p.slug }}>
                      {p.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                  Analytics
                </DropdownMenuLabel>
                {productsAnalytics.map((p) => (
                  <DropdownMenuItem key={p.slug} asChild>
                    <Link to="/products/$slug" params={{ slug: p.slug }}>
                      {p.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                  Agentic execution
                </DropdownMenuLabel>
                {productsAgentic.map((p) => (
                  <DropdownMenuItem key={p.slug} asChild>
                    <Link to="/products/$slug" params={{ slug: p.slug }}>
                      {p.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/lp/intelligent-pr">Intelligent PR</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:text-white transition-colors outline-none">
                Resources <ChevronDown className="size-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {resources.map((r) => (
                  <DropdownMenuItem key={r.to} asChild>
                    <Link to={r.to}>{r.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {primaryLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                activeProps={{ className: "text-white" }}
                className="hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2 lg:gap-3">
          {/* Sign in is for returning users, so it moves into the mobile menu
              and cedes the cramped bar to the primary CTA. */}
          <a
            href={signInUrl(pageSlug)}
            className="hidden lg:inline-flex text-sm font-medium text-white/70 hover:text-white px-3 py-1.5 transition-colors"
          >
            Sign in
          </a>
          <BookDemoButton className="px-4 py-2 text-xs lg:px-5 lg:text-sm">
            Book demo
          </BookDemoButton>
          <button
            aria-label="Toggle menu"
            className="lg:hidden p-2 -mr-2 text-white"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-white/10">
          <nav className="px-6 py-4 flex flex-col gap-3 text-sm text-white/85">
            <Link to="/use-cases" onClick={() => setOpen(false)}>
              Use cases
            </Link>
            <Link to="/products" onClick={() => setOpen(false)}>
              Products
            </Link>
            {[...resources, ...primaryLinks].map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}>
                {l.label}
              </Link>
            ))}
            {/* Book demo already sits in the bar at this breakpoint. */}
            <a
              href={signInUrl(pageSlug)}
              onClick={() => setOpen(false)}
              className="mt-1 pt-3 border-t border-white/10 text-white/70"
            >
              Sign in
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
